import { UNASSESSED_COLOR } from "../labels";

export interface Segment {
  key: string;
  text: string;
  color: string;
  count: number;
}

function StackedBar({ title, segments, total }: { title: string; segments: Segment[]; total: number }) {
  const visible = segments.filter((s) => s.count > 0);
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <h4 className="text-sm font-medium text-foreground-800">{title}</h4>
        <span className="text-xs text-foreground-500 tabular-nums">{total.toLocaleString("pt-BR")} exemplares</span>
      </div>
      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-background-100"
        role="img"
        aria-label={`${title}: ${visible.map((s) => `${s.text} ${s.count}`).join(", ")}`}
      >
        {visible.map((s) => (
          <div
            key={s.key}
            style={{ width: `${(s.count / Math.max(total, 1)) * 100}%`, backgroundColor: s.color }}
            title={`${s.text}: ${s.count}`}
          />
        ))}
      </div>
      <ul className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
        {visible.map((s) => (
          <li key={s.key} className="flex items-center gap-2 text-xs text-foreground-600">
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
            <span className="truncate">{s.text}</span>
            <span className="ml-auto font-semibold text-foreground-900 tabular-nums">{s.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Condição do inventário: saúde e risco de cada exemplar vêm da última vistoria
 * VALIDADA. Exemplar sem vistoria validada aparece como "Sem avaliação" em cinza,
 * nunca como bom ou baixo risco.
 */
export default function ConditionCard({
  health,
  risk,
  withoutAssessment,
  totalTrees,
}: {
  health: Segment[];
  risk: Segment[];
  withoutAssessment: number;
  totalTrees: number;
}) {
  const none: Segment = { key: "none", text: "Sem avaliação", color: UNASSESSED_COLOR, count: withoutAssessment };
  const empty = totalTrees === 0;

  return (
    <section className="bg-white rounded-card border border-background-200 p-5 md:p-6">
      <div className="mb-5">
        <h3 className="text-base font-semibold text-foreground-900 font-heading">Condição do inventário</h3>
        <p className="text-xs text-foreground-500 mt-0.5">Última vistoria validada de cada exemplar</p>
      </div>
      {empty ? (
        <p className="text-sm text-foreground-500 py-6 text-center">Nenhum exemplar cadastrado ainda.</p>
      ) : (
        <div className="space-y-6">
          <StackedBar title="Estado fitossanitário" segments={[...health, none]} total={totalTrees} />
          <StackedBar title="Classe de risco" segments={[...risk, none]} total={totalTrees} />
        </div>
      )}
    </section>
  );
}
